import React, { useRef, useState } from "react";
import { ImagePlus, Upload, X } from "lucide-react";
import { fileToBase64, MAX_IMAGE_MB } from "../utils/Utils";

/**
 * Campo de imagen: subir un archivo o pegar una URL.
 *
 * El archivo se convierte a base64 aquí y viaja dentro del JSON del formulario;
 * es el backend quien lo sube a imgbb y guarda la URL (ver utils/images.js).
 * Por eso `value` puede ser tanto un data URI recién elegido como una URL ya
 * guardada, y el padre no necesita distinguirlos.
 */
function ImageField({
  label = "Imagen (opcional)",
  value = "",
  onChange,
  hint = "Sube un archivo o pega la URL de una imagen.",
}) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");

  const isUpload = value.startsWith("data:");

  const handleFile = async (e) => {
    const file = e.target.files[0];
    // Se limpia el input para que elegir el mismo archivo dos veces dispare el change
    e.target.value = "";
    if (!file) return;

    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      alert(`La imagen no puede pesar más de ${MAX_IMAGE_MB} MB.`);
      return;
    }

    try {
      onChange(await fileToBase64(file));
      setFileName(file.name);
    } catch (error) {
      console.error("Error al leer la imagen:", error);
      alert("No se ha podido leer la imagen. Prueba con otro archivo.");
    }
  };

  const clear = () => {
    onChange("");
    setFileName("");
  };

  return (
    <div className="dyd-field">
      <label className="dyd-label">{label}</label>

      <div className="dyd-image-field">
        <div className="dyd-image-field-preview">
          {value ? (
            <img src={value} alt="Vista previa" />
          ) : (
            <ImagePlus size={22} strokeWidth={1.5} />
          )}
        </div>

        <div className="dyd-image-field-actions">
          <button
            type="button"
            className="dyd-btn dyd-btn-ghost dyd-btn-sm"
            onClick={() => inputRef.current?.click()}
          >
            <Upload size={15} />
            {value ? "Cambiar imagen" : "Subir imagen"}
          </button>

          {value && (
            <button type="button" className="dyd-btn dyd-btn-subtle dyd-btn-sm" onClick={clear}>
              <X size={15} />
              Quitar
            </button>
          )}

          {isUpload && (
            <small className="dyd-hint dyd-image-field-file">
              {fileName || "Imagen seleccionada"}
            </small>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFile}
        />
      </div>

      {/* Con un archivo elegido no mostramos el campo de URL: contendría el
          data URI entero, que son miles de caracteres ilegibles */}
      {!isUpload && (
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…o pega aquí la URL de una imagen"
          className="dyd-input"
        />
      )}

      <small className="dyd-hint">{hint}</small>
    </div>
  );
}

export default ImageField;
